import { Track } from "@/src/lib/tracking";
import { ChatStore } from "@store/modules/chat";
import { rootStore } from "@store/rootStore";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { errorCode, getErrors } from "@config/error";
import { OspToast, deviceDetector } from "@ospweb/sdk-ui";
import { decodeString, encodeString, isEncoded } from "../utils/stringEncoder";
import { useCallback, useContext, useRef, useState } from "react";

import EdgenDrawerCallable from "../components/EdgenDrawerCallable";
import { I18nUtil } from "../utils/i18n";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { UpgradeReminderModalCreateAble } from "../components/modal/UpgradeReminder";
import { updateIsDeepSearchForAgent } from "./useIsDeepSearchAgent";
import { useChatCreditCheck } from "./useChatCreditCheck";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";
import { useLoginProcess } from "./useLoginProcess";
import { useMemoizedFn } from "ahooks";
import { useOpenChatModalWithClose } from "./useOpenChatModal";
import { useSearchEquity } from "./useSearchEquity";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useSnapshot } from "valtio";

export type RouterNavigationMethod =
  | "push"
  | "replace"
  | "back"
  | "forward"
  | "refresh";

export type IUseCreateChatProps = {
  createChatError?: () => void;
  isPage?: boolean;
  /**
   * 场景标记（当前仅 onboarding 有分流逻辑：跳过额度检查）。
   * 历史 scene 值（plan_guide / plan_goal_update / goal_review 等）已随 reference
   * 协议统一废弃 —— 场景路由一律走消息首行协议标记，不再依赖 URL scene。
   */
  scene?: string;
};

type IOnCreateProps = {
  question?: string;
  sign?: string;
  afterCreate?: () => void;
  agentId?: string;
  tpl?: string;
  cacheQuestion?: string;
  shareId?: string;
  routerType?: RouterNavigationMethod;
  localePrefix?: string;
  customParams?: Record<string, any>;
  firstChat?: boolean;
  /**
   * 是否在创建 chatId 后自动提交首条消息（默认 true）
   * - true: 走现有自动发送逻辑（通过 localStorage.is_submit_chat 触发）
   * - false: 仅预填输入框，不自动发送
   */
  autoSubmit?: boolean;
};
export type IChatInfoProps = {
  chatId?: string;
  key?: string;
  tpl?: string;
  cacheQuestion?: string;
  sign?: string;
  scope?: "page" | "modal";
  autoSubmit?: boolean;
  /** 打开 Chat 时 Sender 默认进入 Second opinion 模式 */
  secondOpinion?: boolean;
} | null;

export type IOnCreateParams = {
  question?: string;
  targetChatId?: string;
  sign?: string;
  afterCreate?: () => void;
  agentId?: string;
  tpl?: string;
  cacheQuestion?: string;
  shareId?: string;
  routerType?: RouterNavigationMethod;
  localePrefix?: string;
  customParams?: Record<string, any>;
  firstChat?: boolean;
  /**
   * 是否在创建 chatId 后自动提交首条消息（默认 true）
   */
  autoSubmit?: boolean;
  /**
   * 是否强制新建会话（默认 false）
   * - true: 清除 localStorage chat_id，不复用历史最新会话
   */
  autoRenew?: boolean;

  shouldCreate?: boolean; // 新增参数，控制是否执行创建逻辑，默认为 true
  /**
   * 打开 Chat 时 Sender 默认进入 Second opinion 模式（默认 false）
   */
  secondOpinion?: boolean;
};

export const useCreateChat = (props?: IUseCreateChatProps) => {
  const errors = getErrors();
  const { createChatError, isPage = true, scene } = props || {};
  const [hasError, setHasError] = useState(false);
  const router = useShellRouter();
  const { chatApi } = useContext(OspApiClientContext);
  const [loading, setLoading] = useState(false);
  const chatInfoRef = useRef<IChatInfoProps>(null);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { handleOpenChatModal } = useOpenChatModalWithClose();

  const { getCreditConfig } = useSearchEquity();
  const { checkCredit } = useChatCreditCheck();

  const executeCreateChat = useCallback(
    async ({
      question = "",
      sign,
      afterCreate,
      agentId,
      tpl,
      cacheQuestion,
      shareId,
      routerType = "push",
      customParams,
      autoSubmit = true,
      firstChat,
    }: IOnCreateProps) => {
      if (scene !== "onboarding") {
        const { blocked } = await checkCredit(question);

        if (blocked) {
          setLoading(false);
          return;
        }
      }
      // 解码 quesiotn
      let decodeQuestion = question;
      // 编码 quesiotn
      let encodeQuestion = question;
      ChatStore.initCustomParams = undefined;
      try {
        if (isEncoded(question)) {
          decodeQuestion = decodeString(decodeQuestion);
        } else {
          encodeQuestion = encodeString(decodeQuestion);
        }
      } catch (err) {}
      try {
        const res = await chatApi?.chatId.chatIdCreate({
          agentId,
          shareId,
          firstChat,
        });
        ChatStore.initChat = decodeQuestion;
        const chatId = res?.data?.data?.chatId;
        if (!chatId) {
          Track.log("error", "useCreateChat", "Missing chatId from response", {
            cause: String(res),
          });
          chatInfoRef.current = null;
          setLoading(false);
          createChatError?.();
          setHasError(true);
          return;
        }
        const currentLocale = I18nUtil.getCurrentLocale();
        // Build URL with parameters
        const buildUrl = (
          path: string,
          params: Record<string, string> = {},
        ) => {
          const url = new URL(path, window.location.origin);
          Object.entries(params).forEach(([key, value]) => {
            if (value) {
              url.searchParams.set(key, value);
            }
          });
          return url.pathname + url.search;
        };

        const params: Record<string, string> = {};
        if (encodeQuestion) {
          params.key = sign ? `${encodeQuestion}_${sign}` : encodeQuestion;
          if (tpl) {
            params.tpl = tpl;
          }
          if (cacheQuestion) {
            params.cacheQuestion = cacheQuestion;
          }
          if (autoSubmit) {
            localStorage.setItem("is_submit_chat", "1");
          } else {
            localStorage.removeItem("is_submit_chat");
          }
          ChatStore.initCustomParams = customParams;
        }
        if (agentId) {
          params.agentId = agentId;
        }
        afterCreate?.();
        chatInfoRef.current = {
          chatId,
          ...params,
        };
        if (!isPage) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.CHAT_ID, chatId);
          setLoading(false);
          return { chatId };
        }
        await localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
        // Use the App Router's push method with the constructed URL
        const url = buildUrl(`/chat/${chatId}`, params);
        if (routerType === "replace") {
          sessionStorage.setItem(
            "mainPath",
            window.location.origin +
              `${currentLocale === "en" ? "" : `/${currentLocale}`}` +
              url,
          );
        }
        router[routerType](url);
      } catch (res: any) {
        Track.log("error", "useCreateChat", "Create chat error:", {
          arg1: String(res),
          arg2: String(res.error?.code),
        });
        localStorage.removeItem("create_chat");
        chatInfoRef.current = null;
        // 🔴 登录相关错误：需要支持登录恢复，不清理 URL 参数
        if (
          !isLoggedIn &&
          (res.error?.code === errorCode.CREDITS_LIMITED ||
            res.error?.code === errorCode.LIMIT_TIMES)
        ) {
          // 只有在接口拦截登录的场景下才设置恢复参数
          ChatStore.recoverQuestionParams = {
            question: encodeQuestion,
            sign,
            afterCreate,
            agentId,
            tpl,
            cacheQuestion,
            shareId,
            routerType,
            customParams,
          };
          redirectToLoginPage();
          setLoading(false);
          createChatError?.();
          setHasError(true);
          return;
        }
        const config = getCreditConfig?.(res.error?.code);
        if (config) {
          await UpgradeReminderModalCreateAble({
            customConfig: config,
          });
          setLoading(false);
          createChatError?.(); // 修复问题1：权限检查失败时也要重置 isStartLoading
          setHasError(true);
          return;
        }
        const errorItem = errors.find((item) => item.code === res.error?.code);
        // 报错之后，根据错误码，显示对应提示。
        if (errorItem) {
          OspToast.error({
            content: errorItem.msg,
          });
        }
        setLoading(false);
        createChatError?.();
        setHasError(true);
        return;
      }
    },
    [
      chatApi?.chatId,
      checkCredit,
      createChatError,
      errors,
      getCreditConfig,
      isLoggedIn,
      isPage,
      router,
    ],
  );

  const handleLoginSuccess = useCallback(async () => {
    const params = ChatStore.recoverQuestionParams;
    if (params) {
      // 立即清理参数，防止重复执行
      ChatStore.recoverQuestionParams = undefined;
      executeCreateChat(params);
      setLoading(false);
    }
  }, [executeCreateChat]);

  useLoginProcess(
    handleLoginSuccess,
    { waitForEquity: true }, // 等待权益数据更新完成
  );

  // 原来的 onCreate 方法 重命名 onCreateOrg，为了 ChatModalContent 中不考虑 handleOpenChatModal 能够调用
  const onCreateOrg = useMemoizedFn(async (params: IOnCreateParams) => {
    updateIsDeepSearchForAgent(params.agentId);
    setLoading(true);
    return await executeCreateChat(params);
  });

  const onCreate = useMemoizedFn(async (params: IOnCreateParams) => {
    const { shouldCreate = true } = params;
    if (deviceDetector.isMobile()) {
      /// h5 上, 是 useOpenChatModal 打开的单例弹窗, 状态在其他组件上不同步, 模拟Loading状态
      if (loading) {
        await EdgenDrawerCallable.end(false);
      }
      if (params.shareId) {
        await localStorage.removeItem(LOCAL_STORAGE_KEYS.CHAT_ID);
      }
      setLoading(true);
      // loading的过程中，新发起对话，需要结束上一个弹窗，再新开一个对话。
      setTimeout(
        () => {
          handleOpenChatModal(params);
          setLoading(false);
        },
        loading ? 500 : 0,
      );
    } else {
      if (!shouldCreate) {
        router.push("/chat");
        return;
      }
      await onCreateOrg(params);
    }
  });
  return {
    onCreateOrg,
    onCreate,
    loading,
    chatInfo: chatInfoRef.current,
    hasError,
  };
};
